// Recover from stale dynamically-imported chunks after a deploy.
//
// Vite gives every build content-hashed asset filenames and code-splits lazy
// imports (e.g. React.lazy(() => import("./RichTextMarkdown.jsx"))) into their own
// chunks. When a new build deploys, the old hashed chunks are replaced. A browser
// tab that loaded the PREVIOUS index.html still references the old hashes, so the
// first lazy import after the deploy 404s with "Failed to fetch dynamically imported
// module" and the feature (e.g. the report modal) fails to open.
//
// Vite dispatches a `vite:preloadError` event on window when its preload helper
// (used for every code-split import) fails. Reloading the page pulls the fresh
// index.html + current hashes, transparently recovering. We reload at most once per
// short window so a chunk that is GENUINELY missing (a broken deploy, not just a
// stale tab) can't spin the page in an infinite reload loop.

const RELOAD_KEY = "sooth:chunkReloadAt";
const RELOAD_COOLDOWN_MS = 10_000;

// Is a reload worth offering or doing right now? True unless one was stamped within the
// cooldown (which means that reload didn't fix it — the chunk is really gone, or the dropped
// rows are bad stored rows rather than a stale bundle — so reloading again would just loop).
// Read-only: it never stamps. Reads sessionStorage defensively: a storage exception (private
// mode, disabled) must never mask the error path, so on failure it falls back to "offer once"
// via `now`. Shared with lib/staleBundle.js, whose guided reload consults it before showing
// the "new version — reload" banner.
export function shouldOfferStaleReload(storage, now) {
  let last = 0;
  try {
    last = Number(storage?.getItem(RELOAD_KEY)) || 0;
  } catch {
    last = 0;
  }
  return now - last >= RELOAD_COOLDOWN_MS;
}

// The sessionStorage object of `win`, or undefined where touching the property itself throws
// (a SecurityError when the browser blocks storage) or there is no window. The readers and
// writers above take the result, so a blocked storage degrades to "offer once" and the Reload
// control still reaches `location.reload()`.
export function readSessionStorage(
  win = typeof globalThis !== "undefined" ? globalThis : undefined,
) {
  try {
    return win?.sessionStorage;
  } catch {
    return undefined;
  }
}

// Record that a reload is happening at `now`, so the next check within the cooldown says no.
// Writes defensively: on a storage exception the worst case is one extra reload.
export function stampReload(storage, now) {
  try {
    storage?.setItem(RELOAD_KEY, String(now));
  } catch {
    /* ignore — worst case we may reload one extra time */
  }
}

export function shouldReloadForChunkError(storage, now) {
  if (!shouldOfferStaleReload(storage, now)) return false;
  stampReload(storage, now);
  return true;
}

// Install the window listener. `win` is injectable for testing; the reload decision
// reads the global Date.now() (unit tests exercise the cooldown by calling
// shouldReloadForChunkError directly with an explicit `now`).
export function installChunkReloadHandler(
  win = typeof window !== "undefined" ? window : undefined,
) {
  if (!win || typeof win.addEventListener !== "function") return;
  win.addEventListener("vite:preloadError", (event) => {
    if (!shouldReloadForChunkError(win.sessionStorage, Date.now())) return;
    // Prevent Vite from also throwing the error to the console/unhandledrejection
    // path — we're handling it by reloading.
    event?.preventDefault?.();
    win.location.reload();
  });
}
